import { useCallback, useMemo } from "react";

import { css, cssMap } from "@compiled/react";
import { useIntl } from "react-intl";

export type SocialShareMenuProps = {
    description: string;
    link: string;
    title: string;
    source?: string | null;
};

type SocialShareActionProps = SocialShareMenuProps & {
    source: string;
};

type SocialShareButtonProps = {
    variant: "facebook" | "twitter" | "linkedin";
    title: string;
    action: SocialShareActionProps;
};

const socialShareButtonStyle = cssMap({
    base: {
        display: "inline-block",
        width: 34,
        height: 34,
        overflow: "hidden"
    },
    facebook: {
        background: `no-repeat url("https://wac-cdn.atlassian.com/assets/img/icons/social-icons/facebook-badge.svg")`,
        backgroundSize: "100%"
    },
    twitter: {
        backgroundImage:
            'url("https://wac-cdn.atlassian.com/assets/img/icons/social-icons/twitter.svg")',
        backgroundRepeat: "no-repeat",
        backgroundSize: "100%",
        backgroundPositionY: "center"
    },
    linkedin: {
        backgroundImage:
            'url("https://wac-cdn.atlassian.com/assets/img/icons/social-icons/linked-in.svg")',
        backgroundRepeat: "no-repeat",
        backgroundSize: "100%"
    }
});

const SocialShareButtonActions = {
    facebook: {
        action: "https://www.facebook.com/dialog/feed",
        params: (props: SocialShareActionProps) => ({
            app_id: "743864389089816",
            caption: props.title,
            link: props.link,
            description: props.description,
            picture: "",
            name: props.title
        })
    },
    twitter: {
        action: "https://twitter.com/share",
        params: (props: SocialShareActionProps) => ({
            text: props.description,
            url: props.link,
            via: props.source
        })
    },
    linkedin: {
        action: "https://www.linkedin.com/shareArticle",
        params: (props: SocialShareActionProps) => ({
            mini: "true",
            url: props.link,
            title: props.title,
            summary: props.description,
            source: props.source
        })
    }
};

export const SocialShareButton = ({
    title,
    variant,
    action
}: SocialShareButtonProps) => {
    const href = useMemo(() => {
        const actionDescriptor = SocialShareButtonActions[variant];
        const url = new URL(actionDescriptor.action);
        const params = new URLSearchParams(actionDescriptor.params(action));
        for (const [key, value] of params.entries()) {
            url.searchParams.set(key, value);
        }
        return url.href;
    }, [variant, action]);
    const clickHandler = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            if (typeof window !== "undefined") {
                window.open(
                    href,
                    "",
                    "menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=400,width=600"
                );
                e.preventDefault();
            }
        },
        [href]
    );
    return (
        <a
            css={[socialShareButtonStyle.base, socialShareButtonStyle[variant]]}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share to Social Media"
            href={href}
            title={title}
            onClick={clickHandler}
        >
            <span style={{ pointerEvents: "none", visibility: "hidden" }}>
                {title}
            </span>
        </a>
    );
};

const socialShareMenuItemStyle = css({
    height: "34px",
    marginInLineStart: "5px",
    padding: "2px"
});

const socialShareMenuStyle = css({
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    alignItems: "center"
});

const shareTextStyle = css({
    color: "#253858",
    display: "flex",
    flexDirection: "column",
    textAlign: "left"
});

export const SocialShareMenu = ({
    description,
    link,
    title,
    source
}: SocialShareMenuProps) => {
    const intl = useIntl();

    const action: SocialShareActionProps = useMemo(
        () => ({
            title,
            description,
            link,
            source: source ?? "Atlassian"
        }),
        [description, link, title, source]
    );

    return (
        <div
            css={css({
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
            })}
        >
            <div css={shareTextStyle} className="eyebrow-md">
                {intl.formatMessage({
                    id: "Agile.article.share",
                    defaultMessage: "Share this article"
                })}
            </div>

            <ul css={socialShareMenuStyle}>
                <li css={socialShareMenuItemStyle}>
                    <SocialShareButton
                        variant="facebook"
                        action={action}
                        title={intl.formatMessage({
                            id: "Agile.share-menu.facebook",
                            defaultMessage: "Share on Facebook"
                        })}
                    />
                </li>
                <li css={socialShareMenuItemStyle}>
                    <SocialShareButton
                        variant="twitter"
                        action={action}
                        title={intl.formatMessage({
                            id: "Agile.share-menu.twitter",
                            defaultMessage: "Share on Twitter"
                        })}
                    />
                </li>
                <li css={socialShareMenuItemStyle}>
                    <SocialShareButton
                        variant="linkedin"
                        action={action}
                        title={intl.formatMessage({
                            id: "Agile.share-menu.linkedin",
                            defaultMessage: "Share on LinkedIn"
                        })}
                    />
                </li>
            </ul>
        </div>
    );
};
